'use client';
import React, { useState } from 'react';
import { Zap, CheckCircle, XCircle, RefreshCw, Settings, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: GET /api/integrations to fetch connection states and health
const integrations = [
  {
    id: 'int-servicejockey',
    name: 'ServiceJockey',
    description: 'Job source for service work orders and maintenance tickets. Jobs created in ServiceJockey automatically sync to FleetJockey so trucks and personnel can be dispatched. Do not create service jobs in FleetJockey — use ServiceJockey.',
    status: 'connected',
    health: 'healthy',
    lastSync: '2 min ago',
    syncedRecords: { jobs: 847, customers: 124, assets: 23 },
    version: 'v2.4.1',
    color: 'sky',
    features: ['Job sync (read-only)', 'Customer data', 'Asset registry', 'Status updates', 'Invoice sync'],
  },
  {
    id: 'int-rigjockey',
    name: 'RigJockey',
    description: 'Job source for rig schedules, well site moves, and drilling programs. Rig setup and teardown jobs created in RigJockey sync to FleetJockey for truck and crew dispatch. Do not create rig jobs in FleetJockey — use RigJockey.',
    status: 'connected',
    health: 'warning',
    lastSync: '18 min ago',
    syncedRecords: { jobs: 312, rigs: 8, sites: 47 },
    version: 'v1.9.3',
    color: 'violet',
    features: ['Rig schedule sync (read-only)', 'Well site data', 'Crew manifests', 'Equipment assignments'],
    warningMessage: 'Sync delay detected — last successful sync 18 min ago (expected every 5 min)',
  },
  {
    id: 'int-gps-provider',
    name: 'Samsara Fleet GPS',
    description: 'Primary GPS telematics provider. Streams real-time vehicle positions, speed, engine data, and geofence events. Samsara is a subscription-based service — pricing is per vehicle per month. Contact Samsara for current fleet pricing.',
    status: 'connected',
    health: 'healthy',
    lastSync: '12 sec ago',
    syncedRecords: { vehicles: 8, pings: 14420 },
    version: 'API v2',
    color: 'emerald',
    features: ['Real-time GPS', 'Engine diagnostics', 'Geofencing', 'Driver behavior', 'Fuel monitoring'],
  },
  {
    id: 'int-fuel-card',
    name: 'WEX Fleet Cards',
    description: 'WEX is a fleet fuel card program that gives drivers a dedicated card for fuel and maintenance purchases. FleetJockey syncs WEX transaction data to automatically reconcile fuel purchases against vehicle odometer and GPS location for cost-per-mile reporting.',
    status: 'disconnected',
    health: 'disconnected',
    lastSync: 'Never',
    syncedRecords: {},
    version: '--',
    color: 'orange',
    features: ['Fuel transaction sync', 'Odometer reconciliation', 'Cost per mile', 'Fraud detection'],
  },
  {
    id: 'int-quickbooks',
    name: 'QuickBooks Online',
    description: 'Export completed job cost data, mileage reports, and equipment utilization to QuickBooks for billing and accounting.',
    status: 'disconnected',
    health: 'disconnected',
    lastSync: 'Never',
    syncedRecords: {},
    version: '--',
    color: 'teal',
    features: ['Job cost export', 'Mileage export', 'Invoice creation', 'Customer sync'],
  },
];

const healthConfig = {
  healthy: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Healthy' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', label: 'Warning' },
  disconnected: { icon: XCircle, color: 'text-zinc-500', bg: 'bg-zinc-800 border-zinc-700', label: 'Disconnected' },
};

const colorMap: Record<string, string> = {
  sky: 'border-sky-500/30 bg-sky-500/5',
  violet: 'border-violet-500/30 bg-violet-500/5',
  emerald: 'border-emerald-500/30 bg-emerald-500/5',
  orange: 'border-zinc-700 bg-zinc-800/50',
  teal: 'border-zinc-700 bg-zinc-800/50',
};

export default function IntegrationCards() {
  const [syncing, setSyncing] = useState<string | null>(null);

  const handleSync = async (id: string) => {
    setSyncing(id);
    await new Promise((r) => setTimeout(r, 1500));
    setSyncing(null);
    toast.success('Sync completed successfully');
  };

  const handleConnect = (name: string) => {
    toast.info(`Opening OAuth flow for ${name}...`);
  };

  const handleDisconnect = (name: string) => {
    toast.error(`${name} disconnected`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4 sm:gap-5">
      {integrations.map((integration) => {
        const health = healthConfig[integration.health as keyof typeof healthConfig];
        const HealthIcon = health.icon;
        const isConnected = integration.status === 'connected';
        const isSyncing = syncing === integration.id;

        return (
          <div
            key={integration.id}
            className={`bg-zinc-900 border rounded-xl p-4 sm:p-5 flex flex-col gap-4 transition-all hover:border-zinc-700 ${integration.health === 'warning' ? 'border-amber-500/30' : colorMap[integration.color]}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-zinc-800 border border-zinc-700 flex-shrink-0">
                  <Zap size={18} className="text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-100">{integration.name}</p>
                  {integration.version !== '--' && (
                    <p className="text-[10px] text-zinc-600 font-mono">{integration.version}</p>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-1.5 text-xs font-medium border rounded-full px-2.5 py-1 flex-shrink-0 ${health.bg} ${health.color}`}>
                <HealthIcon size={11} />
                <span className="hidden sm:inline">{health.label}</span>
              </div>
            </div>

            {/* Warning */}
            {integration.warningMessage && (
              <div className="flex items-start gap-2 bg-amber-950/20 border border-amber-500/20 rounded-lg px-3 py-2">
                <AlertTriangle size={11} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-amber-300">{integration.warningMessage}</p>
              </div>
            )}

            {/* Description */}
            <p className="text-xs text-zinc-500 leading-relaxed">{integration.description}</p>

            {/* Features */}
            <div className="flex flex-wrap gap-1">
              {integration.features.map((f) => (
                <span key={`feat-${integration.id}-${f}`} className="text-[10px] bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5 text-zinc-500">{f}</span>
              ))}
            </div>

            {/* Sync stats */}
            {isConnected && Object.keys(integration.syncedRecords).length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(integration.syncedRecords).map(([key, val]) => (
                  <div key={`stat-${integration.id}-${key}`} className="bg-zinc-800 rounded-lg px-2.5 py-2 text-center">
                    <p className="text-sm font-bold text-zinc-200 tabular-nums">{(val as number).toLocaleString()}</p>
                    <p className="text-[10px] text-zinc-600 capitalize">{key}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Last sync */}
            {isConnected && (
              <p className="text-[11px] text-zinc-600 flex items-center gap-1">
                <RefreshCw size={9} /> Last sync: {integration.lastSync}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1 border-t border-zinc-800 mt-auto flex-wrap">
              {isConnected ? (
                <>
                  <button
                    onClick={() => handleSync(integration.id)}
                    disabled={isSyncing}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-medium text-zinc-300 rounded-lg transition-all disabled:opacity-60"
                  >
                    <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                    {isSyncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-medium text-zinc-300 rounded-lg transition-all">
                    <Settings size={11} /> Configure
                  </button>
                  <button
                    onClick={() => handleDisconnect(integration.name)}
                    className="ml-auto px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnect(integration.name)}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold rounded-lg transition-all active:scale-95 w-full justify-center"
                >
                  <ExternalLink size={12} /> Connect {integration.name}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}